import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Bot,
  CheckCheck,
  Clock3,
  Headphones,
  MessageCircle,
  Paperclip,
  Plus,
  Send,
  UserRound,
  X,
} from "lucide-react";
import {
  SupportClient,
  AttachmentScanPending,
  type PendingMessage,
  type AttachmentDraft,
} from "../integrations/supportHub/packages/client-sdk/index";
import {
  SupportError,
  type Conversation,
  type StoredMessage,
} from "../integrations/supportHub/packages/contracts/index";
import { assistantStateSchema } from "../integrations/supportHub/packages/contracts/assistant";
import { z } from "zod";
import { supportCopy } from "./copy";
import "./support.css";

interface Props {
  readonly client: SupportClient | null;
  readonly locale: "th" | "ko" | "en";
  readonly identity: string;
  readonly loginUrl?: string;
}
export function SupportCenterView({
  client,
  locale,
  identity,
  loginUrl,
}: Props): React.JSX.Element {
  const t = supportCopy[locale];
  const [rows, setRows] = useState<Conversation[]>([]),
    [selected, setSelected] = useState<string | null>(null),
    [history, setHistory] = useState<StoredMessage[]>([]);
  const [routing, setRouting] = useState<z.infer<
      typeof assistantStateSchema
    > | null>(null),
    [draft, setDraft] = useState(""),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [pending, setPending] = useState<PendingMessage | null>(null),
    [attachments, setAttachments] = useState<AttachmentDraft[]>([]),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(client !== null),
    [more, setMore] = useState(false);
  const [online, setOnline] = useState(navigator.onLine),
    [listOpen, setListOpen] = useState(client !== null);
  const current = useRef(selected),
    epoch = useRef(0),
    end = useRef<HTMLDivElement>(null);
  const storageKey = `decobaan:support:${identity}`;
  const selectedRow = rows.find((r) => r.id === selected);
  const mode =
    routing?.mode === "human"
      ? t.human
      : routing?.mode === "waiting_human"
        ? t.waiting
        : routing?.review === "ai_resolved_review"
          ? t.review
          : routing === null
            ? t.waiting
            : t.ai;
  const minuteLabel = (minute: number) =>
    `${Math.floor(minute / 60)
      .toString()
      .padStart(2, "0")}:${(minute % 60).toString().padStart(2, "0")}`;
  const hours =
    routing === null
      ? t.hours
      : t.hours.replace(
          "09:00–18:00",
          `${minuteLabel(routing.startMinute)}–${minuteLabel(routing.endMinute)}`,
        );
  useEffect(() => {
    current.current = selected;
  }, [selected]);
  useEffect(() => {
    const on = () => {
      setOnline(navigator.onLine);
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", on);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", on);
    };
  }, []);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (draft.trim() || pending !== null || attachments.length > 0)
        event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
    };
  }, [draft, pending, attachments]);
  const messageError = useCallback(
    (cause: unknown) =>
      cause instanceof SupportError && cause.code === "support_not_configured"
        ? t.unavailable
        : t.error,
    [t],
  );
  const refresh = useCallback(async () => {
    if (client === null) return;
    const version = epoch.current,
      conversationId = current.current;
    try {
      const next = await client.listConversations();
      if (epoch.current !== version) return;
      setRows((prev) =>
        [...new Map([...prev, ...next].map((c) => [c.id, c])).values()].sort(
          (a, b) => b.updatedAt.localeCompare(a.updatedAt),
        ),
      );
      setMore(next.length === 100);
      if (conversationId !== null) {
        const [messages, state] = await Promise.all([
          client.replay(conversationId),
          client.assistantState(conversationId),
        ]);
        if (epoch.current !== version || current.current !== conversationId)
          return;
        setHistory(messages);
        setRouting(state);
        if (document.visibilityState === "visible" && document.hasFocus())
          await client.markRead(
            conversationId,
            messages.at(-1)?.messageSeq ?? 0,
          );
      }
      setError("");
    } catch (cause) {
      if (epoch.current === version) {
        setError(messageError(cause));
        if (
          cause instanceof SupportError &&
          [401, 403, 404].includes(cause.status)
        ) {
          setHistory([]);
          setRouting(null);
        }
      }
    } finally {
      if (epoch.current === version) setLoading(false);
    }
  }, [client, messageError]);
  useEffect(() => {
    epoch.current++;
    setRows([]);
    setHistory([]);
    setRouting(null);
    setDraft("");
    setPending(null);
    setAttachments([]);
    setSelected(null);
    current.current = null;
    let active = true;
    if (client !== null) {
      setLoading(true);
      void client
        .listConversations()
        .then((next) => {
          if (!active) return;
          setRows(next);
          setMore(next.length === 100);
          let stored: string | null = null;
          try {
            stored = localStorage.getItem(storageKey);
          } catch {
            /* Session restoration remains available from the server. */
          }
          const restored = next.find((row) => row.id === stored);
          if (restored) {
            current.current = restored.id;
            setSelected(restored.id);
            setListOpen(false);
          }
        })
        .catch((cause: unknown) => {
          if (active) setError(messageError(cause));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }
    return () => {
      active = false;
      client?.clearSession();
    };
  }, [client, storageKey, messageError]);
  useEffect(() => {
    current.current = selected;
    epoch.current++;
    setHistory([]);
    setRouting(null);
    if (selected !== null && client !== null) {
      try {
        localStorage.setItem(storageKey, selected);
      } catch {
        /* History itself is stored on the server. */
      }
      void refresh();
    }
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible" && navigator.onLine)
        void refresh();
    }, 4000);
    return () => {
      window.clearInterval(timer);
    };
  }, [selected, client, storageKey, refresh]);
  useEffect(() => {
    if (history.length > 0 || pending !== null)
      end.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [history.length, pending]);
  function choose(id: string) {
    if (
      (draft.trim() || pending !== null || attachments.length > 0) &&
      !window.confirm(t.leave)
    )
      return;
    setDraft("");
    setPending(null);
    setAttachments([]);
    current.current = id;
    setSelected(id);
    setListOpen(false);
    setNotice("");
  }
  async function newConversation(question?: string) {
    if (client === null || busy) return;
    if (
      (draft.trim() || pending !== null || attachments.length > 0) &&
      !window.confirm(t.leave)
    )
      return;
    setBusy(true);
    const version = epoch.current;
    try {
      const c = await client.createConversation(t.title);
      if (version !== epoch.current) return;
      setRows((prev) => [c, ...prev]);
      setDraft(question ?? "");
      setPending(null);
      setAttachments([]);
      current.current = c.id;
      setSelected(c.id);
      setListOpen(false);
      setNotice("");
    } catch (cause) {
      if (version === epoch.current) setError(messageError(cause));
    } finally {
      setBusy(false);
    }
  }
  async function send() {
    if (
      client === null ||
      selected === null ||
      busy ||
      !online ||
      attachments.some((a) => a.state !== "ready")
    )
      return;
    const conversationId = selected,
      version = epoch.current;
    const item = pending ?? {
      clientMessageId: crypto.randomUUID(),
      text: draft.trim(),
      attachmentIds: attachments.map((a) => a.id),
      state: "pending",
    };
    if (!item.text && !item.attachmentIds.length) return;
    setBusy(true);
    setPending({ ...item, state: "sending" });
    setError("");
    try {
      await client.send(conversationId, item);
      if (version !== epoch.current) return;
      setDraft("");
      setAttachments([]);
      setPending(null);
      setNotice(t.saved);
      await refresh();
    } catch (cause) {
      if (version === epoch.current) {
        setPending({ ...item, state: "failed" });
        setError(messageError(cause));
      }
    } finally {
      setBusy(false);
    }
  }
  async function upload(file: File) {
    if (client === null || selected === null || busy || attachments.length >= 5)
      return;
    if (
      file.size > 10 * 1024 * 1024 ||
      file.size < 1 ||
      !["image/jpeg", "image/png", "application/pdf", "text/plain"].includes(
        file.type,
      )
    ) {
      setError(t.fileError);
      return;
    }
    const version = epoch.current,
      conversationId = selected;
    setBusy(true);
    try {
      const id = await client.uploadAttachment(conversationId, file);
      if (version === epoch.current)
        setAttachments((prev) => [
          ...prev,
          { id, name: file.name, state: "ready" },
        ]);
    } catch (cause) {
      if (version === epoch.current) {
        if (cause instanceof AttachmentScanPending)
          setAttachments((prev) => [
            ...prev,
            { id: cause.attachmentId, name: file.name, state: "scan-pending" },
          ]);
        else setError(t.fileError);
      }
    } finally {
      setBusy(false);
    }
  }
  async function retryScan(item: AttachmentDraft) {
    if (client === null || busy) return;
    const version = epoch.current;
    setBusy(true);
    try {
      await client.completeAttachment(item.id);
      if (version === epoch.current)
        setAttachments((prev) =>
          prev.map((a) => (a.id === item.id ? { ...a, state: "ready" } : a)),
        );
    } catch {
      if (version === epoch.current) setError(t.fileError);
    } finally {
      setBusy(false);
    }
  }
  async function requestHuman() {
    if (client === null || selected === null || routing === null || busy)
      return;
    const version = epoch.current;
    setBusy(true);
    try {
      const next = await client.requestHuman(selected, routing.version);
      if (version === epoch.current) {
        setRouting(next);
        setNotice(t.humanSaved);
      }
    } catch (cause) {
      if (version === epoch.current) setError(messageError(cause));
    } finally {
      setBusy(false);
    }
  }
  async function download(id: string) {
    if (client === null) return;
    const version = epoch.current;
    try {
      const blob = await client.downloadAttachment(id);
      if (version !== epoch.current) return;
      const url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = `attachment-${id}`;
      a.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch {
      if (version === epoch.current) setError(t.fileError);
    }
  }
  async function loadMore() {
    if (client === null || busy) return;
    const last = rows.at(-1);
    if (!last) return;
    setBusy(true);
    try {
      const next = await client.listConversations(false, {
        before: last.updatedAt,
        beforeId: last.id,
      });
      setRows((prev) => [
        ...new Map([...prev, ...next].map((c) => [c.id, c])).values(),
      ]);
      setMore(next.length === 100);
    } catch {
      setError(t.error);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="db-support">
      <header className="db-support-top">
        <a href={`/?lang=${locale}`} aria-label={t.home}>
          <img src="/brand/decobaan-logo-roof-lowered.png" alt="Decobaan" />
        </a>
        <a href={`/?lang=${locale}`}>
          <ArrowLeft size={16} />
          {t.home}
        </a>
      </header>
      <div className={`db-support-shell ${listOpen ? "is-list" : "is-chat"}`}>
        <aside className="db-support-sidebar">
          <div className="db-support-sidebar-title">
            <span className="db-support-icon">
              <Headphones size={23} />
            </span>
            <div>
              <h1>{t.title}</h1>
              <p>{t.subtitle}</p>
            </div>
          </div>
          <button
            className="db-support-new"
            disabled={client === null || busy}
            onClick={() => {
              void newConversation();
            }}
          >
            <Plus size={18} />
            {t.newChat}
          </button>
          <h2>{t.conversations}</h2>
          <div className="db-support-list">
            {loading ? (
              <p role="status">{t.loading}</p>
            ) : rows.length === 0 ? (
              <p>{t.empty}</p>
            ) : (
              rows.map((row) => (
                <button
                  key={row.id}
                  aria-current={selected === row.id ? "true" : undefined}
                  className={selected === row.id ? "active" : ""}
                  onClick={() => {
                    choose(row.id);
                  }}
                >
                  <MessageCircle size={19} />
                  <span>
                    <strong>{row.title}</strong>
                    <small>
                      {new Intl.DateTimeFormat(locale, {
                        timeZone: "Asia/Bangkok",
                        month: "short",
                        day: "numeric",
                      }).format(new Date(row.updatedAt))}{" "}
                      · {row.assigneeId === null ? t.waiting : t.human}
                    </small>
                  </span>
                  {row.unreadCount > 0 ? <b>{row.unreadCount}</b> : null}
                </button>
              ))
            )}
          </div>
          {more ? (
            <button
              disabled={busy}
              onClick={() => {
                void loadMore();
              }}
            >
              {t.more}
            </button>
          ) : null}
          <div className="db-support-hours">
            <Clock3 size={19} />
            <div>
              <strong>{hours}</strong>
              <p>{t.hoursBody}</p>
            </div>
          </div>
        </aside>
        <section className="db-support-chat" aria-label={t.title}>
          <header className="db-support-chat-header">
            <button
              className="db-support-back"
              onClick={() => {
                setListOpen(true);
              }}
              aria-label={t.back}
            >
              <ArrowLeft size={21} />
            </button>
            <span className="db-support-icon">
              {routing?.mode === "human" ? (
                <UserRound size={23} />
              ) : (
                <Headphones size={23} />
              )}
            </span>
            <div>
              <h2>{t.title}</h2>
              <p className="db-support-status">
                {selected === null ? t.subtitle : mode}
              </p>
            </div>
            {selected !== null && routing?.mode !== "human" ? (
              <button
                className="db-support-human"
                disabled={routing === null || busy}
                onClick={() => {
                  void requestHuman();
                }}
              >
                <UserRound size={16} />
                {t.requestHuman}
              </button>
            ) : null}
          </header>
          {!online ? (
            <p className="db-support-alert" role="status">
              {t.offline}
            </p>
          ) : null}
          {error ? (
            <div className="db-support-alert" role="alert">
              {error}
              <button
                onClick={() => {
                  void refresh();
                }}
              >
                {t.retry}
              </button>
            </div>
          ) : null}
          <div
            className="db-support-messages"
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
          >
            {history.length === 0 ? (
              <div className="db-support-welcome">
                <span className="db-support-welcome-icon">
                  <MessageCircle size={32} />
                </span>
                <h2>{t.welcome}</h2>
                <p>{client === null ? t.loginBody : t.welcomeBody}</p>
                {client === null ? (
                  <a
                    className="db-support-login"
                    href={loginUrl ?? `/login?lang=${locale}&next=/support`}
                  >
                    {t.login}
                  </a>
                ) : (
                  <div className="db-support-questions">
                    {t.questions.map((question) => (
                      <button
                        key={question}
                        disabled={pending !== null || busy}
                        onClick={() => {
                          if (selected === null) void newConversation(question);
                          else setDraft(question);
                        }}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              history.map((message) => (
                <article
                  key={message.messageId}
                  className={`db-support-message ${message.senderKind === "customer" ? "from-customer" : "from-team"}`}
                >
                  <span className="db-support-author">
                    {message.senderKind === "ai" ? <Bot size={15} /> : null}
                    {message.senderKind === "customer"
                      ? t.you
                      : message.senderKind === "ai"
                        ? t.aiName
                        : t.agent}
                  </span>
                  <div className="db-support-bubble">
                    {message.text}
                    {message.attachmentIds.map((id) => (
                      <button
                        className="db-support-file"
                        key={id}
                        onClick={() => {
                          void download(id);
                        }}
                      >
                        <Paperclip size={15} />
                        {t.download}
                      </button>
                    ))}
                  </div>
                  <time dateTime={message.serverCreatedAt}>
                    {new Intl.DateTimeFormat(locale, {
                      timeZone: "Asia/Bangkok",
                      hour: "2-digit",
                      minute: "2-digit",
                    }).format(new Date(message.serverCreatedAt))}
                    {message.senderKind === "customer" ? (
                      <CheckCheck size={14} aria-label={t.saved} />
                    ) : null}
                  </time>
                </article>
              ))
            )}
            {pending !== null ? (
              <article className="db-support-message from-customer">
                <div className="db-support-bubble">{pending.text}</div>
                <button
                  disabled={busy || !online}
                  onClick={() => {
                    void send();
                  }}
                >
                  {pending.state === "sending" ? t.sending : t.failed}
                </button>
              </article>
            ) : null}
            <div ref={end} />
          </div>
          <footer className="db-support-composer">
            <p className="db-support-disclaimer">
              <Bot size={15} />
              {t.note}
            </p>
            {selectedRow?.status === "closed" ? (
              <p>{t.close}</p>
            ) : (
              <>
                <div className="db-support-attachments">
                  {attachments.map((a) => (
                    <span key={a.id}>
                      {a.name}
                      {a.state === "scan-pending" ? (
                        <button
                          disabled={busy}
                          onClick={() => {
                            void retryScan(a);
                          }}
                        >
                          {t.scan} · {t.retry}
                        </button>
                      ) : null}
                      <button
                        aria-label={t.remove}
                        disabled={busy || pending !== null}
                        onClick={() => {
                          setAttachments((prev) =>
                            prev.filter((item) => item.id !== a.id),
                          );
                        }}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void send();
                  }}
                >
                  <label
                    className="db-support-attach"
                    aria-label={t.attach}
                    title={t.attachmentHelp}
                  >
                    <Paperclip size={21} />
                    <input
                      type="file"
                      aria-label={t.attach}
                      accept="image/jpeg,image/png,application/pdf,text/plain"
                      disabled={
                        selected === null ||
                        busy ||
                        pending !== null ||
                        attachments.length >= 5
                      }
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        event.currentTarget.value = "";
                        if (file !== undefined) void upload(file);
                      }}
                    />
                  </label>
                  <textarea
                    aria-label={t.placeholder}
                    placeholder={t.placeholder}
                    rows={2}
                    maxLength={4000}
                    value={draft}
                    disabled={
                      client === null ||
                      selected === null ||
                      busy ||
                      pending !== null
                    }
                    onChange={(event) => {
                      setDraft(event.target.value);
                    }}
                    onKeyDown={(event) => {
                      if (
                        event.key === "Enter" &&
                        (event.ctrlKey || event.metaKey)
                      ) {
                        event.preventDefault();
                        void send();
                      }
                    }}
                  />
                  <button
                    type="submit"
                    className="db-support-send"
                    aria-label={t.send}
                    disabled={
                      client === null ||
                      selected === null ||
                      busy ||
                      !online ||
                      attachments.some((a) => a.state !== "ready") ||
                      (!draft.trim() &&
                        attachments.length === 0 &&
                        pending === null)
                    }
                  >
                    <Send size={20} />
                  </button>
                </form>
                <small>{t.attachmentHelp}</small>
              </>
            )}
            <p className="db-support-notice" role="status">
              {notice}
            </p>
          </footer>
        </section>
      </div>
    </main>
  );
}
